/**
 * Routing Fields Adapter
 *
 * Converts ROUTING_FIELDS and CRM fields to FieldPickerPopover format.
 * Supports multi-source mode with sidebar (Record, Analysis, CRM Deal, CRM Lead, etc.)
 */

import {
  BarChart3,
  Phone,
  FileText,
  Building2,
  User,
  Users,
  Calendar,
} from 'lucide-react';
import { createElement } from 'react';
import type { Field } from 'react-querybuilder';
import i18n from '@/i18n/config';
import type { PickerField, PickerFieldGroup, PickerFieldSource } from '../types';

/**
 * Source IDs for routing fields
 */
export const ROUTING_SOURCE_IDS = {
  record: 'record',
  analysis: 'analysis',
  crm_deal: 'crm-deal',
  crm_lead: 'crm-lead',
  crm_contact: 'crm-contact',
  crm_company: 'crm-company',
  metadata: 'metadata',
} as const;

/**
 * Source configurations with icons and labels
 */
const SOURCE_CONFIG: Record<
  string,
  { i18nKey: string; icon: React.ReactNode; order: number }
> = {
  [ROUTING_SOURCE_IDS.record]: {
    i18nKey: 'settings:routingFieldPicker.sources.record',
    icon: createElement(Phone, { className: 'h-4 w-4' }),
    order: 1,
  },
  [ROUTING_SOURCE_IDS.analysis]: {
    i18nKey: 'settings:routingFieldPicker.sources.analysis',
    icon: createElement(BarChart3, { className: 'h-4 w-4' }),
    order: 2,
  },
  [ROUTING_SOURCE_IDS.crm_deal]: {
    i18nKey: 'settings:routingFieldPicker.sources.crmDeal',
    icon: createElement(FileText, { className: 'h-4 w-4' }),
    order: 3,
  },
  [ROUTING_SOURCE_IDS.crm_lead]: {
    i18nKey: 'settings:routingFieldPicker.sources.crmLead',
    icon: createElement(User, { className: 'h-4 w-4' }),
    order: 4,
  },
  [ROUTING_SOURCE_IDS.crm_contact]: {
    i18nKey: 'settings:routingFieldPicker.sources.crmContact',
    icon: createElement(Users, { className: 'h-4 w-4' }),
    order: 5,
  },
  [ROUTING_SOURCE_IDS.crm_company]: {
    i18nKey: 'settings:routingFieldPicker.sources.crmCompany',
    icon: createElement(Building2, { className: 'h-4 w-4' }),
    order: 6,
  },
  [ROUTING_SOURCE_IDS.metadata]: {
    i18nKey: 'settings:routingFieldPicker.sources.metadata',
    icon: createElement(Calendar, { className: 'h-4 w-4' }),
    order: 7,
  },
};

/**
 * Map group name to source ID
 */
function groupToSourceId(group: string): string {
  const normalized = group
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  switch (normalized) {
    case 'record_data':
    case 'record':
      return ROUTING_SOURCE_IDS.record;
    case 'analysis_results':
    case 'analysis':
      return ROUTING_SOURCE_IDS.analysis;
    case 'crm_deal':
    case 'crm_deal_':
    case 'crmdeal':
      return ROUTING_SOURCE_IDS.crm_deal;
    case 'crm_lead':
    case 'crmlead':
      return ROUTING_SOURCE_IDS.crm_lead;
    case 'crm_contact':
    case 'crmcontact':
      return ROUTING_SOURCE_IDS.crm_contact;
    case 'crm_company':
    case 'crmcompany':
      return ROUTING_SOURCE_IDS.crm_company;
    case 'metadata':
      return ROUTING_SOURCE_IDS.metadata;
    default:
      return ROUTING_SOURCE_IDS.metadata;
  }
}

/**
 * Field descriptions for better UX
 */
const FIELD_DESCRIPTIONS: Record<string, string> = {
  'record.channel': 'recordChannel',
  'record.direction': 'recordDirection',
  'record.duration': 'recordDuration',
  'record.agentId': 'recordAgentId',
  'record.departmentId': 'recordDepartmentId',
  'record.externalId': 'recordExternalId',
  'analysis.qaScore': 'analysisQaScore',
  'analysis.sentimentScore': 'analysisSentimentScore',
  'analysis.category': 'analysisCategory',
  'analysis.hasAlert': 'analysisHasAlert',
  'metadata.projectId': 'metadataProjectId',
  'metadata.dayOfWeek': 'metadataDayOfWeek',
  'metadata.hourOfDay': 'metadataHourOfDay',
};

/**
 * Map react-querybuilder inputType to PickerField inputType
 */
function mapInputType(
  inputType?: string | null
): 'text' | 'number' | 'select' | 'date' | 'boolean' {
  if (!inputType) return 'text';
  switch (inputType) {
    case 'number':
      return 'number';
    case 'select':
      return 'select';
    case 'date':
      return 'date';
    default:
      return 'text';
  }
}

/**
 * Convert a single Field to PickerField
 */
function fieldToPickerField(field: Field): PickerField {
  const sourceId = groupToSourceId((field.group as string) || 'metadata');
  const group = sourceId;
  return {
    name: field.name,
    label: field.label,
    group,
    inputType: mapInputType(field.inputType),
    values: field.values as Array<{ name: string; label: string }> | undefined,
    description: FIELD_DESCRIPTIONS[field.name]
      ? i18n.t(
          `settings:routingFieldPicker.fieldDescriptions.${FIELD_DESCRIPTIONS[field.name]}`,
          field.label as string
        )
      : undefined,
    defaultValue: field.defaultValue as string | number | boolean | undefined,
    disabled: false,
  };
}

/**
 * Convert routing fields to FieldPickerPopover simple format (fields + groups)
 * Use this for simpler UI without source sidebar
 */
export function routingFieldsToPickerFormat(routingFields: Field[]): {
  fields: PickerField[];
  groups: PickerFieldGroup[];
} {
  const fields = routingFields.map(fieldToPickerField);

  // Extract unique groups
  const uniqueGroups = new Set<string>();
  fields.forEach((field) => uniqueGroups.add(field.group));

  const groups: PickerFieldGroup[] = Array.from(uniqueGroups).map((groupKey) => {
    const sourceId = groupToSourceId(groupKey);
    const config = SOURCE_CONFIG[sourceId] || {
      i18nKey: 'settings:routingFieldPicker.groups.other',
      icon: undefined,
      order: 99,
    };
    return {
      key: groupKey,
      label: i18n.t(config.i18nKey, groupKey),
      icon: config.icon,
      order: config.order,
      defaultCollapsed: false,
    };
  });

  return { fields, groups };
}

/**
 * Convert routing fields to FieldPickerPopover multi-source format
 * Use this for full UI with source sidebar
 */
export function routingFieldsToSourcesFormat(
  routingFields: Field[]
): PickerFieldSource[] {
  // Group fields by source
  const fieldsBySource = new Map<string, PickerField[]>();

  for (const field of routingFields) {
    const group = (field.group as string) || 'Other';
    const sourceId = groupToSourceId(group);
    const pickerField = fieldToPickerField(field);

    if (!fieldsBySource.has(sourceId)) {
      fieldsBySource.set(sourceId, []);
    }
    fieldsBySource.get(sourceId)!.push(pickerField);
  }

  // Build sources array
  const sources: PickerFieldSource[] = [];

  for (const [sourceId, fields] of fieldsBySource) {
    const config = SOURCE_CONFIG[sourceId];
    if (!config) continue;

    // For each source, create a single "All Fields" group
    const groups: PickerFieldGroup[] = [
      {
        key: 'all',
        label: i18n.t('settings:routingFieldPicker.groups.allFields', 'All Fields'),
        order: 1,
        defaultCollapsed: false,
      },
    ];

    // Update all fields to use the "all" group within this source
    const fieldsWithGroup = fields.map((f) => ({
      ...f,
      group: 'all',
    }));

    sources.push({
      id: sourceId,
      label: i18n.t(config.i18nKey),
      icon: config.icon,
      groups,
      fields: fieldsWithGroup,
    });
  }

  // Sort by order
  sources.sort((a, b) => {
    const orderA = SOURCE_CONFIG[a.id]?.order || 99;
    const orderB = SOURCE_CONFIG[b.id]?.order || 99;
    return orderA - orderB;
  });

  return sources;
}

/**
 * Helper: Check if any CRM fields are present
 */
export function hasCrmFields(routingFields: Field[]): boolean {
  return routingFields.some((f) => f.name.startsWith('crm.'));
}

/**
 * Helper: Count fields by source
 */
export function countFieldsBySource(routingFields: Field[]): Record<string, number> {
  const counts: Record<string, number> = {};

  for (const field of routingFields) {
    const group = (field.group as string) || 'Other';
    const sourceId = groupToSourceId(group);
    counts[sourceId] = (counts[sourceId] || 0) + 1;
  }

  return counts;
}
